import { useEffect, useMemo, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Loader2, Search, UserPlus, X } from "lucide-react";
import { toast } from "sonner";
import {
  useAddManualEvent,
  useCustomers,
  useEventTypes,
} from "../lib/customers";
import { parseDealValue } from "../lib/parse-deal-value";

/** Current local time as a value for <input type="datetime-local">. */
function nowLocalInputValue(): string {
  const d = new Date();
  d.setSeconds(0, 0);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
}

export interface FixedVisitor {
  visitorId: number;
  label: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: number;
  /** Prefills the event-type field (e.g. the active event-type tab). */
  defaultEventType?: string;
  /** Locks the visitor (single-visitor view) instead of showing the search. */
  fixedVisitor?: FixedVisitor;
}

type SelectedVisitor =
  | { kind: "existing"; visitorId: number; label: string }
  | { kind: "new"; email?: string; phone?: string; label: string };

function looksLikeEmail(q: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(q);
}

function looksLikePhone(q: string): boolean {
  return /^\+?[0-9][0-9 ()\/.-]*$/.test(q) && q.replace(/\D/g, "").length >= 6;
}

export function AddEventDialog({
  open,
  onOpenChange,
  projectId,
  defaultEventType,
  fixedVisitor,
}: Props) {
  const [visitor, setVisitor] = useState<SelectedVisitor | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [eventType, setEventType] = useState("");
  const [valueRaw, setValueRaw] = useState("");
  const [currency, setCurrency] = useState("EUR");
  const [datetime, setDatetime] = useState(nowLocalInputValue());

  // Reset the form every time the dialog opens, applying prefills.
  useEffect(() => {
    if (!open) return;
    setVisitor(
      fixedVisitor
        ? { kind: "existing", ...fixedVisitor }
        : null,
    );
    setSearch("");
    setDebouncedSearch("");
    setEventType(defaultEventType ?? "");
    setValueRaw("");
    setCurrency("EUR");
    setDatetime(nowLocalInputValue());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const searchQuery = useCustomers({
    projectId:
      open && !fixedVisitor && !visitor && debouncedSearch.length >= 2
        ? projectId
        : null,
    q: debouncedSearch || undefined,
    limit: 8,
    offset: 0,
  });

  const eventTypesQuery = useEventTypes(open ? projectId : null);
  const knownEventTypes = eventTypesQuery.data?.items ?? [];

  const add = useAddManualEvent(projectId);

  const parsedValue = useMemo(
    () => (valueRaw.trim() ? parseDealValue(valueRaw) : null),
    [valueRaw],
  );
  const valueInvalid = valueRaw.trim() !== "" && parsedValue === null;

  const canSubmit =
    Boolean(visitor) &&
    eventType.trim().length > 0 &&
    datetime.length > 0 &&
    !valueInvalid &&
    !add.isPending;

  const submit = () => {
    if (!visitor || !canSubmit) return;
    const createdAt = new Date(datetime);
    if (Number.isNaN(createdAt.getTime())) {
      toast.error("Datum/Uhrzeit ungültig");
      return;
    }
    add.mutate(
      {
        ...(visitor.kind === "existing"
          ? { visitorId: visitor.visitorId }
          : { newVisitor: { email: visitor.email, phone: visitor.phone } }),
        eventType: eventType.trim(),
        createdAt: createdAt.toISOString(),
        ...(parsedValue !== null
          ? { dealValue: parsedValue, dealCurrency: currency.trim() || "EUR" }
          : {}),
      },
      {
        onSuccess: () => {
          toast.success(
            visitor.kind === "new"
              ? "Event in die Pipeline geschrieben — der neue Visitor erscheint in wenigen Sekunden in der Liste."
              : "Event in die Pipeline geschrieben — erscheint in wenigen Sekunden in der Journey.",
          );
          onOpenChange(false);
        },
        onError: (err) =>
          toast.error(`Event anlegen fehlgeschlagen: ${(err as Error).message}`),
      },
    );
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-overlay bg-overlay animate-fade-in" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-modal w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-md border border-border-primary bg-bg-elevated shadow-overlay animate-fade-in">
          <div className="flex items-center justify-between border-b border-border-primary px-5 py-3">
            <Dialog.Title className="font-medium text-text-primary">
              Event hinzufügen
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Schließen"
                className="text-text-tertiary hover:text-text-primary"
              >
                <X size={16} />
              </button>
            </Dialog.Close>
          </div>

          <div className="space-y-4 px-5 py-4">
            {/* Visitor */}
            <div>
              <label className="mb-1.5 block text-sm font-medium text-text-primary">
                Visitor
              </label>
              {visitor ? (
                <div className="flex items-center gap-2 rounded-md border border-border-primary bg-bg-secondary px-3 py-2">
                  {visitor.kind === "new" && (
                    <span className="rounded-full bg-accent/15 px-1.5 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-wider text-accent">
                      neu
                    </span>
                  )}
                  <span className="flex-1 truncate text-sm text-text-primary">
                    {visitor.label}
                  </span>
                  {visitor.kind === "existing" ? (
                    <span className="font-mono text-[10px] text-text-tertiary">
                      id {visitor.visitorId}
                    </span>
                  ) : (
                    <span className="text-[10px] text-text-tertiary">
                      wird neu angelegt
                    </span>
                  )}
                  {!fixedVisitor && (
                    <button
                      type="button"
                      onClick={() => setVisitor(null)}
                      aria-label="Visitor abwählen"
                      className="text-text-tertiary hover:text-danger"
                    >
                      <X size={13} />
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 rounded-md border border-border-primary bg-bg-primary px-3 py-2">
                    <Search size={13} className="shrink-0 text-text-tertiary" />
                    <input
                      type="search"
                      autoFocus
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Name, Email oder Telefon suchen…"
                      className="flex-1 bg-transparent text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none"
                    />
                    {searchQuery.isFetching && (
                      <Loader2
                        size={13}
                        className="animate-spin text-text-tertiary"
                      />
                    )}
                  </div>
                  {debouncedSearch.length >= 2 && searchQuery.data && (
                    <ul className="mt-1 max-h-48 overflow-auto rounded-md border border-border-primary bg-bg-primary">
                      {searchQuery.data.items.length === 0 ? (
                        <NoMatchCreateSuggestion
                          query={debouncedSearch}
                          onCreate={(v) => setVisitor(v)}
                        />
                      ) : (
                        searchQuery.data.items.map((c) => (
                          <li key={c.visitorId}>
                            <button
                              type="button"
                              onClick={() =>
                                setVisitor({
                                  kind: "existing",
                                  visitorId: c.visitorId,
                                  label: c.displayName,
                                })
                              }
                              className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-bg-hover"
                            >
                              <span className="flex-1 truncate text-sm text-text-primary">
                                {c.displayName}
                              </span>
                              <span className="shrink-0 text-[10px] text-text-tertiary">
                                {c.lastEventType} ·{" "}
                                {new Date(c.lastActionAt).toLocaleDateString(
                                  "de-DE",
                                )}
                              </span>
                              <span className="shrink-0 font-mono text-[10px] text-text-tertiary">
                                id {c.visitorId}
                              </span>
                            </button>
                          </li>
                        ))
                      )}
                    </ul>
                  )}
                </>
              )}
            </div>

            {/* Event-Typ */}
            <div>
              <label className="mb-1.5 block text-sm font-medium text-text-primary">
                Event
              </label>
              <input
                type="text"
                list="add-event-type-suggestions"
                value={eventType}
                onChange={(e) => setEventType(e.target.value)}
                placeholder="z.B. Purchase — Vorschläge oder Freitext"
                className="h-9 w-full rounded-md border border-border-primary bg-bg-primary px-3 text-sm text-text-primary focus:border-border-focus focus:outline-none"
              />
              <datalist id="add-event-type-suggestions">
                {knownEventTypes.map((t) => (
                  <option key={t.eventType} value={t.eventType} />
                ))}
              </datalist>
            </div>

            {/* Value + Währung */}
            <div className="grid grid-cols-[1fr_90px] gap-2">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-text-primary">
                  Value (optional)
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={valueRaw}
                  onChange={(e) => setValueRaw(e.target.value)}
                  placeholder="z.B. 1.997,50 oder 1997.50"
                  className={`h-9 w-full rounded-md border bg-bg-primary px-3 font-mono text-sm text-text-primary focus:outline-none ${
                    valueInvalid
                      ? "border-danger"
                      : "border-border-primary focus:border-border-focus"
                  }`}
                />
                <p
                  className={`mt-1 text-xs ${valueInvalid ? "text-danger" : "text-text-tertiary"}`}
                >
                  {valueInvalid
                    ? "Wert nicht lesbar — bitte Zahl eingeben."
                    : parsedValue !== null
                      ? `Wird gespeichert als: ${parsedValue.toLocaleString(
                          "de-DE",
                          { minimumFractionDigits: 2 },
                        )} ${currency.trim() || "EUR"}`
                      : "Punkt oder Komma — beides wird erkannt."}
                </p>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-text-primary">
                  Währung
                </label>
                <input
                  type="text"
                  value={currency}
                  maxLength={3}
                  onChange={(e) =>
                    setCurrency(
                      e.target.value.toUpperCase().replace(/[^A-Z]/g, ""),
                    )
                  }
                  className="h-9 w-full rounded-md border border-border-primary bg-bg-primary px-3 text-center font-mono text-sm text-text-primary focus:border-border-focus focus:outline-none"
                />
              </div>
            </div>

            {/* Datum + Uhrzeit */}
            <div>
              <label className="mb-1.5 block text-sm font-medium text-text-primary">
                Datum &amp; Uhrzeit
              </label>
              <input
                type="datetime-local"
                value={datetime}
                onChange={(e) => setDatetime(e.target.value)}
                className="h-9 w-full rounded-md border border-border-primary bg-bg-primary px-3 text-sm text-text-primary focus:border-border-focus focus:outline-none"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 border-t border-border-primary bg-bg-secondary px-5 py-3">
            <Dialog.Close asChild>
              <button
                type="button"
                className="rounded-md border border-border-primary bg-bg-primary px-3 py-1.5 text-sm font-medium text-text-primary hover:bg-bg-hover"
              >
                Abbrechen
              </button>
            </Dialog.Close>
            <button
              type="button"
              onClick={submit}
              disabled={!canSubmit}
              className="inline-flex items-center gap-1 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-text-on-accent hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              {add.isPending && <Loader2 size={13} className="animate-spin" />}
              Event speichern
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * Shown when the visitor search has no hits. If the query is a complete
 * email or phone number, offer to mint a new visitor with that key —
 * otherwise explain what to type.
 */
function NoMatchCreateSuggestion({
  query,
  onCreate,
}: {
  query: string;
  onCreate: (v: SelectedVisitor) => void;
}) {
  const email = looksLikeEmail(query) ? query.toLowerCase() : null;
  const phone = !email && looksLikePhone(query) ? query : null;

  if (!email && !phone) {
    return (
      <li className="px-3 py-2 text-xs text-text-tertiary">
        Kein Visitor gefunden. Gib eine vollständige Email-Adresse oder
        Telefonnummer ein, um einen neuen Visitor mit diesem Event anzulegen.
      </li>
    );
  }

  return (
    <li>
      <button
        type="button"
        onClick={() =>
          onCreate({
            kind: "new",
            ...(email ? { email } : {}),
            ...(phone ? { phone } : {}),
            label: email ?? phone ?? query,
          })
        }
        className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-bg-hover"
      >
        <UserPlus size={14} className="shrink-0 text-accent" />
        <span className="flex-1 text-sm text-text-primary">
          Kein Treffer — neuen Visitor mit{" "}
          {email ? "dieser Email" : "dieser Telefonnummer"} anlegen:{" "}
          <span className="font-mono text-xs">{email ?? phone}</span>
        </span>
      </button>
    </li>
  );
}
